import { AssignmentListComponent } from '@agent/components/assignment-list/assignment-list.component';
import { RolodexNoteViewDialogComponent } from '@agent/components/notes/rolodex-note-view-dialog/rolodex-note-view-dialog.component';
import { SearchBarActiveToggleComponent } from '@agent/layout/search-bar/search-bar-active-toggle/search-bar-active-toggle.component';
import { SearchBarGetStartedComponent } from '@agent/layout/search-bar/search-bar-get-started.component';
import { StateService } from '@infra/state/state.service';
import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject, type OnInit, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, type FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { type AssignmentDTO, type SearchDTO, type SearchItemDTO } from '@api/models';
import { AssignmentService } from '@api/services/assignment.service';
import { SearchService } from '@api/services/search.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { KeyboardClickableDirective } from '@common/directives/keyboard-clickable.directive';
import { PermissionDirective } from '@common/directives/permission.directive';
import { CountryNameComponent } from '@shared/addresses/country-name/country-name.component';
import { CountrySelectComponent } from '@shared/addresses/country-select/country-select.component';
import { PillComponent } from '@common/ui/pill/pill.component';
import { AuthService } from '@infra/auth/auth.service';
import { Globals } from '@system/globals';
import { sortBy } from 'lodash';
import { EMPTY, type Observable, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { InitialsPipe } from '@common/pipes/initials.pipe';

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  imports: [
    InitialsPipe,
    KeyboardClickableDirective,
    FormsModule,
    ReactiveFormsModule,
    SearchBarActiveToggleComponent,
    TranslatePipe,
    SearchBarGetStartedComponent,
    NgClass,
    RouterLink,
    PermissionDirective,
    AssignmentListComponent,
    CountryNameComponent,
    RolodexNoteViewDialogComponent,
    CountrySelectComponent,
    PillComponent,
  ],
})
export class SearchBarComponent implements OnInit {
  readonly authService = inject(AuthService);
  private readonly stateService = inject(StateService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly modalService = inject(NgbModal);
  private readonly assignmentService = inject(AssignmentService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly onlyActiveCustomersLocalStorageKey = 'search_only_active_customers';

  private readonly chooseCustomerTriggered = new Subject<SearchItemDTO>();
  private readonly chooseAccountTriggered = new Subject<SearchItemDTO>();
  private readonly chooseBookingAgentTriggered = new Subject<SearchItemDTO>();
  private readonly searchTriggered = new Subject<{ term: string; stayOnCurrentTab: boolean }>();
  showSearchPopup = false;
  searchTerm$: Subject<string> = new Subject<string>();
  searchTerm = '';

  selectedBookingAgent: SearchItemDTO = null;
  selectedAccount: SearchItemDTO = null;
  selectedCustomer: SearchItemDTO = null;

  customers: SearchItemDTO[] = [];
  bookingAgents: SearchItemDTO[] = [];
  accounts: SearchItemDTO[] = [];

  rdgform: FormGroup;
  searching = false;

  isCustomerExist = 0;
  isBookingAgentExist = 0;
  isAccountExist = 0;

  searchInfo: string;
  assignmentsTitle: string;

  activetab: string;
  globals = Globals;

  bookingAgentAssignments: AssignmentDTO[];
  accountAssignments: AssignmentDTO[];
  customerAssignments: AssignmentDTO[];
  selectedRolodexCompanyId: string;
  showSearch = true;
  onlyActiveCustomers = false;

  selectedCountryCode: string;

  private hoverTimeout: any;

  constructor() {
    this.assignmentsTitle = '$assignments$';
    this.activetab = 'customers';
    this.selectedBookingAgent = null;
    this.selectedCustomer = null;
    this.selectedAccount = null;

    this.stateService.showSearch$.pipe(takeUntilDestroyed()).subscribe((show: boolean) => {
      this.showSearch = show;
    });
  }

  ngOnInit(): void {
    this.chooseCustomerTriggered
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.onChooseCustomer(value);
      });

    this.chooseBookingAgentTriggered
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.onChooseBookingAgent(value);
      });

    this.chooseAccountTriggered
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.onChooseAccount(value);
      });

    const onlyActiveCustomersLocalStorage = localStorage.getItem(this.onlyActiveCustomersLocalStorageKey);

    if (onlyActiveCustomersLocalStorage) {
      this.onlyActiveCustomers = onlyActiveCustomersLocalStorage === 'true';
    }

    this.searchTerm$.pipe(debounceTime(1250), takeUntilDestroyed(this.destroyRef)).subscribe(term => {
      this.search(term);
    });

    this.searchTriggered
      .pipe(
        switchMap(({ term, stayOnCurrentTab }) => this.searchAll(term, stayOnCurrentTab)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ searchResult, stayOnCurrentTab }) => {
        this.setBookingAgents(searchResult.bookingAgents);
        this.setAccounts(searchResult.accounts);
        this.setCustomers(searchResult.customers);

        if (!stayOnCurrentTab) {
          if (searchResult.customers.length > 0) this.activetab = 'customers';
          else if (searchResult.bookingAgents.length > 0) this.activetab = 'bookingagents';
          else if (searchResult.accounts.length > 0) this.activetab = 'accounts';
        }
      });

    this.stateService.showSearchPopup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((show: boolean) => {
      this.showSearchPopup = show;

      // Clear term state when closed
      if (!show) {
        this.searchTerm$.next('');
        this.searchTerm = '';
        this.emptySearchBar();
      }
    });

    this.stateService.searching$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isSearching: boolean) => {
      this.searching = isSearching;
    });

    this.rdgform = this.fb.group({
      searchTerm: [this.searchTerm],
    });
  }

  keyup(event: KeyboardEvent): void {
    const txt = /^[a-zA-Z]+$/;
    const num = /^[0-9]+$/;
    const term = (event.target as HTMLInputElement).value;
    this.searchInfo = 'm';
    if (term.match(txt)) {
      this.searchInfo = 't';
    }
    if (term.match(num)) {
      this.searchInfo = 'n';
    }
    this.searchTerm = term;
    this.searchTerm$.next(term);

    if (event.which !== 13) {
      this.stateService.setSearching(true);
    }

    if (term.length < 2) {
      this.stateService.setSearching(false);
      this.setCustomers([]);
      this.setBookingAgents([]);
      this.setAccounts([]);
    }
  }

  clickSearchBox(): void {
    if (this.searchTerm === '') {
      this.searchInfo = '';
    }
    this.isCustomerExist = 1;
    this.isBookingAgentExist = 1;
    this.isAccountExist = 1;

    // Don't do anything if searchPopup is already open
    if (!this.showSearchPopup) {
      this.selectedCustomer = null;
      this.selectedBookingAgent = null;
      this.selectedAccount = null;
      this.customers = [];
      this.bookingAgents = [];
      this.accounts = [];

      // Show search
      this.stateService.showSearchPopup(true);
      this.stateService.setSearching(true);

      // Re-search term if searchbar is not empty
      const search = this.searchTerm;
      if (search != undefined && search.length > 0) {
        this.searchTerm$.next(search);
      } else {
        this.stateService.setSearching(false);
        this.setCustomers([]);
        this.setBookingAgents([]);
        this.setAccounts([]);
      }
    }
  }

  closeSearch(): void {
    this.stateService.showSearchPopup(false);
    this.searchTerm = '';
    this.searchTerm$.next('');
    this.emptySearchBar();
    this.selectedCustomer = null;
    this.setCustomers([]);
    this.selectedBookingAgent = null;
    this.setBookingAgents([]);
    this.setAccounts([]);
    this.selectedAccount = null;
    this.customers = [];
    this.bookingAgents = [];
    this.accounts = [];
    this.searching = null;
    this.activetab = 'customers';
  }

  onChooseCustomer(customer: SearchItemDTO): void {
    if (this.selectedCustomer !== customer) {
      this.customerAssignments = [];
      this.selectedCustomer = customer;
      if (this.selectedCustomer.assignmentIds.length > 0) {
        this.selectedCustomer.assignmentIds.forEach(assignmentId => {
          this.assignmentService.getAssignment(assignmentId, true).subscribe((assignment: AssignmentDTO) => {
            this.customerAssignments.push(assignment);
            this.customerAssignments = sortBy(this.customerAssignments, 'addedDateTime');
          });
        });
      } else {
        this.assignmentService.getAssignments(customer.id, true).subscribe((assignments: AssignmentDTO[]) => {
          this.customerAssignments = assignments;
          this.customerAssignments = sortBy(this.customerAssignments, 'addedDateTime');
        });
      }
    }
  }

  chooseCustomer(customer: SearchItemDTO): boolean {
    this.chooseCustomerTriggered.next(customer);
    return true;
  }

  onCustomerMouseEnter(customer: SearchItemDTO): void {
    this.hoverTimeout = setTimeout(() => {
      this.chooseCustomer(customer);
    }, 300);
  }

  onCustomerMouseLeave(): void {
    clearTimeout(this.hoverTimeout);
  }

  viewCustomer(customer: SearchItemDTO): void {
    this.stateService.showSearchPopup(false);
    this.router.navigate([`/${this.authService.companyUrl}/customers/${customer.id}`]);

    this.searchTerm = '';
    this.searchTerm$.next('');
    this.emptySearchBar();
    this.activetab = 'customers';
  }

  chooseBookingAgent(bookingagent: SearchItemDTO): void {
    this.chooseBookingAgentTriggered.next(bookingagent);
  }

  onBookingAgentMouseEnter(bookingagent: SearchItemDTO): void {
    this.hoverTimeout = setTimeout(() => {
      this.chooseBookingAgent(bookingagent);
    }, 300);
  }

  onBookingAgentMouseLeave(): void {
    clearTimeout(this.hoverTimeout);
  }

  onChooseBookingAgent(bookingagent: SearchItemDTO): void {
    if (this.selectedBookingAgent !== bookingagent) {
      this.bookingAgentAssignments = [];
      this.selectedBookingAgent = bookingagent;

      if (this.selectedBookingAgent.assignmentIds.length > 0) {
        this.selectedBookingAgent.assignmentIds.forEach(assignmentId => {
          this.assignmentService.getAssignment(assignmentId, true).subscribe((assignment: AssignmentDTO) => {
            this.bookingAgentAssignments.push(assignment);
            this.bookingAgentAssignments = sortBy(this.bookingAgentAssignments, 'addedDateTime');
          });
        });
      } else {
        this.assignmentService
          .getAssignmentsByPartnerId(bookingagent.id, true)
          .subscribe((assignments: AssignmentDTO[]) => {
            this.bookingAgentAssignments = assignments;
            this.bookingAgentAssignments = sortBy(this.bookingAgentAssignments, 'addedDateTime');
          });
      }
    }
  }

  viewBookingAgent(bookingagent: SearchItemDTO): void {
    this.stateService.showSearchPopup(false);
    this.router.navigate([`/${this.authService.companyUrl}/partners/${bookingagent.id}`]);

    this.searchTerm = '';
    this.searchTerm$.next('');
    this.emptySearchBar();

    this.activetab = 'bookingagents';
  }

  onAccountMouseEnter(account: SearchItemDTO): void {
    this.hoverTimeout = setTimeout(() => {
      this.chooseAccount(account);
    }, 300);
  }

  onAccountMouseLeave(): void {
    clearTimeout(this.hoverTimeout);
  }

  onChooseAccount(account: SearchItemDTO): void {
    if (this.selectedAccount !== account) {
      this.accountAssignments = [];
      this.selectedAccount = account;
      if (this.selectedAccount.assignmentIds.length > 0) {
        this.selectedAccount.assignmentIds.forEach(assignmentId => {
          this.assignmentService.getAssignment(assignmentId, true).subscribe((assignment: AssignmentDTO) => {
            this.accountAssignments.push(assignment);
            this.accountAssignments = sortBy(this.accountAssignments, 'addedDateTime');
          });
        });
      } else {
        this.assignmentService.getAssignmentsByAccountId(account.id, true).subscribe((assignments: AssignmentDTO[]) => {
          this.accountAssignments = assignments;
          this.accountAssignments = sortBy(this.accountAssignments, 'addedDateTime');
        });
      }
    }
  }

  chooseAccount(account: SearchItemDTO): void {
    this.chooseAccountTriggered.next(account);
  }

  viewAccount(account: SearchItemDTO): void {
    this.stateService.showSearchPopup(false);

    this.router.navigate([`/${this.authService.companyUrl}/accounts/${account.id}`]);

    this.searchTerm = '';
    this.searchTerm$.next('');
    this.emptySearchBar();

    this.activetab = 'accounts';
  }

  onCustomerAssignmentClosed(): void {
    this.assignmentService.getAssignments(this.selectedCustomer.id).subscribe((assignments: AssignmentDTO[]) => {
      this.customerAssignments = assignments;
      this.customerAssignments = sortBy(this.customerAssignments, 'addedDateTime');
    });
  }

  onAgentAssignmentClosed(): void {
    this.assignmentService.getAssignments(this.selectedBookingAgent.id).subscribe((assignments: AssignmentDTO[]) => {
      this.bookingAgentAssignments = assignments;
      this.bookingAgentAssignments = sortBy(this.bookingAgentAssignments, 'addedDateTime');
    });
  }

  onAccountAssignmentClosed(): void {
    this.assignmentService.getAssignments(this.selectedAccount.id).subscribe((assignments: AssignmentDTO[]) => {
      this.accountAssignments = assignments;
      this.accountAssignments = sortBy(this.accountAssignments, 'addedDateTime');
    });
  }

  openRolodexNoteView(event: Event, content: TemplateRef<Element>, rolodexCompanyId: string): void {
    event.stopPropagation();
    this.selectedRolodexCompanyId = rolodexCompanyId;
    this.modalService.open(content, { size: 'md' });
  }

  toggleOnlyActiveCustomers() {
    this.onlyActiveCustomers = !this.onlyActiveCustomers;

    localStorage.setItem(this.onlyActiveCustomersLocalStorageKey, this.onlyActiveCustomers.toString());

    this.search(this.searchTerm);
  }

  onCountryCodeChange(): void {
    this.search(this.searchTerm, true);
  }

  private emptySearchBar(): void {
    if (this.rdgform) {
      this.rdgform.patchValue({ searchTerm: '' });
      this.searchTerm = '';
    }
  }

  private setCustomers(customers: SearchItemDTO[]): void {
    if (customers !== undefined && customers.length > 0) {
      this.customers = customers;
      this.chooseCustomer(customers[0]);
    } else {
      this.customers = [];
      this.selectedCustomer = null;
    }
    this.customers = customers;
  }

  private setBookingAgents(bookingagents: SearchItemDTO[]): void {
    if (bookingagents != undefined && bookingagents.length > 0) {
      this.bookingAgents = bookingagents;
      this.chooseBookingAgent(bookingagents[0]);
    } else {
      this.bookingAgents = [];
      this.selectedBookingAgent = null;
    }
    this.bookingAgents = bookingagents;
  }

  private setAccounts(accounts: SearchItemDTO[]): void {
    if (accounts != undefined && accounts.length > 0) {
      this.accounts = accounts;
      this.chooseAccount(accounts[0]);
    } else {
      this.accounts = [];
      this.selectedAccount = null;
    }
    this.accounts = accounts;
  }

  private search(term: string, stayOnCurrentTab = false): void {
    this.searchTriggered.next({ term, stayOnCurrentTab });
  }

  private searchAll(
    term: string,
    stayOnCurrentTab: boolean
  ): Observable<{ searchResult: SearchDTO; stayOnCurrentTab: boolean }> {
    this.setCustomers([]);
    this.setBookingAgents([]);
    this.setAccounts([]);

    if (!term || term.length < 2) {
      this.stateService.setSearching(false);
      return EMPTY;
    }

    this.stateService.setSearching(true);

    // The error interceptor already shows the failure; swallow it so the next search still runs.
    return this.searchService.searchAll(term.trim(), this.onlyActiveCustomers, this.selectedCountryCode).pipe(
      map(searchResult => ({ searchResult, stayOnCurrentTab })),
      catchError(() => EMPTY),
      finalize(() => this.stateService.setSearching(false))
    );
  }
}
